import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const AuthenticationForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    domain: '',
    username: '',
    totpCode: '',
    rememberDevice: false
  });
  const [authStep, setAuthStep] = useState('credentials'); // 'credentials', 'passkey', 'totp'
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [totpTimer, setTotpTimer] = useState(0);
  const [isPasskeySupported, setIsPasskeySupported] = useState(false);

  // Mock credentials for authentication
  const mockCredentials = {
    domain: 'securevault.com',
    username: 'admin@securevault.com',
    totpCode: '123456'
  };

  useEffect(() => {
    // Check WebAuthn support
    if (window.PublicKeyCredential) {
      setIsPasskeySupported(true);
    }
  }, []);

  useEffect(() => {
    let interval;
    if (totpTimer > 0) {
      interval = setInterval(() => {
        setTotpTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [totpTimer]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleCredentialsSubmit = async (e) => {
    e?.preventDefault();
    setIsLoading(true);
    setError('');

    // Validate domain and username
    if (!formData?.domain || !formData?.username) {
      setError('Please enter both domain and username');
      setIsLoading(false);
      return;
    }

    if (formData?.domain !== mockCredentials?.domain || formData?.username !== mockCredentials?.username) {
      setError('Invalid domain or username. Use: securevault.com / admin@securevault.com');
      setIsLoading(false);
      return;
    }

    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      setAuthStep('passkey');
    }, 1500);
  };

  const handlePasskeyAuth = async () => {
    setIsLoading(true);
    setError('');

    try {
      // Simulate WebAuthn passkey authentication
      setTimeout(() => {
        setIsLoading(false);
        // Simulate successful authentication
        navigate('/organization-dashboard');
      }, 2000);
    } catch (err) {
      setError('Passkey authentication failed. Please try TOTP instead.');
      setIsLoading(false);
      setAuthStep('totp');
    }
  };

  const handleTotpSubmit = async (e) => {
    e?.preventDefault();
    setIsLoading(true);
    setError('');

    if (!formData?.totpCode) {
      setError('Please enter the TOTP code');
      setIsLoading(false);
      return;
    }

    if (formData?.totpCode !== mockCredentials?.totpCode) {
      setError('Invalid TOTP code. Use: 123456');
      setIsLoading(false);
      return;
    }

    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      navigate('/organization-dashboard');
    }, 1500);
  };

  const handleResendTotp = () => {
    setTotpTimer(30);
    // Simulate sending new TOTP code
    console.log('New TOTP code sent');
  };

  const handleSsoLogin = (provider) => {
    setIsLoading(true);
    // Simulate SSO redirect
    setTimeout(() => {
      setIsLoading(false);
      navigate('/organization-dashboard');
    }, 2000);
  };

  const handleForgotPassword = () => {
    // Navigate to password recovery (mock)
    console.log('Redirecting to password recovery...');
  };

  const renderCredentialsStep = () => (
    <form onSubmit={handleCredentialsSubmit} className="space-y-6">
      <div className="space-y-4">
        <Input
          label="Organization Domain"
          type="text"
          placeholder="your-company.com"
          value={formData?.domain}
          onChange={(e) => handleInputChange('domain', e?.target?.value)}
          required
          disabled={isLoading}
        />

        <Input
          label="Username"
          type="email"
          placeholder="user@your-company.com"
          value={formData?.username}
          onChange={(e) => handleInputChange('username', e?.target?.value)}
          required
          disabled={isLoading}
        />
      </div>

      <Button
        type="submit"
        variant="default"
        loading={isLoading}
        fullWidth
        iconName="ArrowRight"
        iconPosition="right"
      >
        Continue
      </Button>
    </form>
  );

  const renderPasskeyStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Fingerprint" size={32} className="text-primary" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">
          Authenticate with Passkey
        </h3>
        <p className="text-sm text-muted-foreground">
          Use your device's biometric authentication or security key
        </p>
      </div>

      <Button
        variant="default"
        onClick={handlePasskeyAuth}
        loading={isLoading}
        fullWidth
        iconName="Shield"
        iconPosition="left"
      >
        {isLoading ? 'Authenticating...' : 'Use Passkey'}
      </Button>

      <div className="text-center">
        <button
          type="button"
          onClick={() => setAuthStep('totp')}
          className="text-sm text-accent hover:text-accent/80 transition-smooth"
          disabled={isLoading}
        >
          Use TOTP code instead
        </button>
      </div>
    </div>
  );

  const renderTotpStep = () => (
    <form onSubmit={handleTotpSubmit} className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Smartphone" size={32} className="text-accent" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">
          Enter TOTP Code
        </h3>
        <p className="text-sm text-muted-foreground">
          Enter the 6-digit code from your authenticator app
        </p>
      </div>

      <Input
        label="TOTP Code"
        type="text"
        placeholder="000000"
        value={formData?.totpCode}
        onChange={(e) => handleInputChange('totpCode', e?.target?.value?.replace(/\D/g, '')?.slice(0, 6))}
        maxLength={6}
        required
        disabled={isLoading}
        className="text-center text-2xl tracking-widest"
      />

      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          Didn't receive a code?
        </span>
        <button
          type="button"
          onClick={handleResendTotp}
          disabled={totpTimer > 0 || isLoading}
          className="text-accent hover:text-accent/80 transition-smooth disabled:opacity-50"
        >
          {totpTimer > 0 ? `Resend in ${totpTimer}s` : 'Resend Code'}
        </button>
      </div>

      <Button
        type="submit"
        variant="default"
        loading={isLoading}
        fullWidth
        iconName="LogIn"
        iconPosition="right"
      >
        Sign In
      </Button>

      <div className="text-center">
        <button
          type="button"
          onClick={() => setAuthStep('passkey')}
          className="text-sm text-accent hover:text-accent/80 transition-smooth"
          disabled={isLoading}
        >
          Try passkey instead
        </button>
      </div>
    </form>
  );

  const renderSsoOptions = () => (
    <div className="space-y-3">
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-card text-muted-foreground">Or continue with</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Button
          variant="outline"
          onClick={() => handleSsoLogin('saml')}
          disabled={isLoading}
          iconName="Building"
          iconPosition="left"
        >
          SAML SSO
        </Button>
        <Button
          variant="outline"
          onClick={() => handleSsoLogin('oidc')}
          disabled={isLoading}
          iconName="Key"
          iconPosition="left"
        >
          OIDC
        </Button>
      </div>
    </div>
  );

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-card border border-border rounded-lg shadow-card p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
            <Icon name="Shield" size={32} color="white" />
          </div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">
            SecureVault Enterprise
          </h1>
          <p className="text-sm text-muted-foreground">
            Sign in to your secure vault
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-error" />
              <span className="text-sm text-error">{error}</span>
            </div>
          </div>
        )}

        {/* Authentication Steps */}
        {authStep === 'credentials' && renderCredentialsStep()}
        {authStep === 'passkey' && renderPasskeyStep()}
        {authStep === 'totp' && renderTotpStep()}

        {/* SSO Options - Show only on credentials step */}
        {authStep === 'credentials' && (
          <div className="mt-6">
            {renderSsoOptions()}
          </div>
        )}

        {/* Remember Device & Forgot Password */}
        {(authStep === 'passkey' || authStep === 'totp') && (
          <div className="mt-6 space-y-4">
            <Checkbox
              label="Remember this device for 30 days"
              checked={formData?.rememberDevice}
              onChange={(e) => handleInputChange('rememberDevice', e?.target?.checked)}
              disabled={isLoading}
            />

            <div className="text-center">
              <button
                type="button"
                onClick={handleForgotPassword}
                className="text-sm text-accent hover:text-accent/80 transition-smooth"
                disabled={isLoading}
              >
                Forgot your password?
              </button>
            </div>
          </div>
        )}

        {/* Back Button */}
        {authStep !== 'credentials' && (
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setAuthStep('credentials')}
              className="text-sm text-muted-foreground hover:text-foreground transition-smooth"
              disabled={isLoading}
            >
              ← Back to login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthenticationForm;